import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { DollarSign, TrendingUp, Users } from 'lucide-react';
import {
  apiFetch, rangeFromPreset, rangeToQuery, DateRangePicker, StatCard, Skeleton, EmptyState,
  type DateRange,
} from './shared';

interface CampaignRow {
  id: string;
  name: string;
  sourceName: string | null;
  campaign_type: string;
  clicks: number;
  signups: number;
  revenue: number;
  spend: number;
  incentive: number;
  totalCost: number;
  roi: number;
  is_active: boolean;
}

interface FunnelStep {
  step: string;
  count: number;
  rate?: number;
}

export default function OverviewSection({ onSelectCampaign }: { onSelectCampaign: (id: string) => void }) {
  const [range, setRange] = useState<DateRange>(() => rangeFromPreset('30d'));
  const q = rangeToQuery(range);

  const campaignsQ = useQuery({
    queryKey: ['admin', 'ua', 'campaigns', q],
    queryFn: () => apiFetch(`/api/ua/admin/campaigns${q}`) as Promise<{ campaigns: CampaignRow[] }>,
  });
  const statsQ = useQuery({
    queryKey: ['admin', 'ua', 'stats', q],
    queryFn: () => apiFetch(`/api/ua/admin/stats${q}`) as Promise<{ funnel: FunnelStep[] }>,
  });

  const campaigns = campaignsQ.data?.campaigns ?? [];
  const funnel = statsQ.data?.funnel ?? [];

  const totals = useMemo(() => {
    return campaigns.reduce((acc, c) => ({
      revenue: acc.revenue + (c.revenue ?? 0),
      spend: acc.spend + (c.spend ?? 0),
      incentive: acc.incentive + (c.incentive ?? 0),
      signups: acc.signups + (c.signups ?? 0),
    }), { revenue: 0, spend: 0, incentive: 0, signups: 0 });
  }, [campaigns]);
  const totalCost = totals.spend + totals.incentive;
  const netRoi = totalCost > 0 ? ((totals.revenue - totalCost) / totalCost) * 100 : 0;

  const chartData = useMemo(() => {
    const bySource: Record<string, { revenue: number; cost: number }> = {};
    for (const c of campaigns) {
      const src = c.sourceName ?? 'Unknown';
      const entry = bySource[src] ?? { revenue: 0, cost: 0 };
      entry.revenue += c.revenue ?? 0;
      entry.cost += (c.spend ?? 0) + (c.incentive ?? 0);
      bySource[src] = entry;
    }
    return Object.entries(bySource).map(([name, d]) => ({
      name, Revenue: +d.revenue.toFixed(2), Cost: +d.cost.toFixed(2),
    }));
  }, [campaigns]);

  // Funnel steps we surface on Overview.
  const signupStep = funnel.find((s) => s.step === 'Signups');
  const profileStep = funnel.find((s) => s.step === 'Profile Complete');
  const firstSurveyStep = funnel.find((s) => s.step === 'First Survey Complete');
  const firstRedemptionStep = funnel.find((s) => s.step === 'First Redemption');

  const isLoading = campaignsQ.isLoading || statsQ.isLoading;

  return (
    <div className="space-y-6">
      <DateRangePicker value={range} onChange={setRange} />

      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Revenue" value={`$${totals.revenue.toFixed(2)}`} tone="good" />
            <StatCard label="Total Spend" value={`$${(totals.spend + totals.incentive).toFixed(2)}`} tone="bad" sub={`Media $${totals.spend.toFixed(2)} + Incentive $${totals.incentive.toFixed(2)}`} />
            <StatCard label="Net ROI" value={`${netRoi.toFixed(1)}%`} tone={netRoi >= 0 ? 'good' : 'bad'} />
            <StatCard label="Total Signups" value={totals.signups} tone="info" />
          </div>

          <div className="bg-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" /> Funnel
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Signups" value={signupStep?.count ?? 0} sub={signupStep?.rate != null ? `${signupStep.rate}% of clicks` : undefined} />
              <StatCard label="Profile Complete" value={profileStep?.count ?? 0} sub={profileStep?.rate != null ? `${profileStep.rate}% of signups` : undefined} />
              <StatCard label="First Survey" value={firstSurveyStep?.count ?? 0} sub={firstSurveyStep?.rate != null ? `${firstSurveyStep.rate}% of starts` : undefined} />
              <StatCard label="First Redemption" value={firstRedemptionStep?.count ?? 0} sub={firstRedemptionStep?.rate != null ? `${firstRedemptionStep.rate}% of completes` : undefined} />
            </div>
          </div>

          {chartData.length > 0 ? (
            <div className="bg-slate-800 rounded-xl p-5">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary" /> Revenue vs Cost by Advertiser
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#34d399" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Cost" fill="#f87171" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState title="No campaign data in this range yet" body="Create an advertiser and campaign to start tracking paid performance." />
          )}

          <div className="bg-slate-800 rounded-xl overflow-x-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" /> Campaigns in range
              </h3>
              <span className="text-xs text-slate-400">{campaigns.length} campaign{campaigns.length === 1 ? '' : 's'}</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Advertiser</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Clicks</th>
                  <th className="px-3 py-2 text-right">Signups</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Spend</th>
                  <th className="px-3 py-2 text-right">ROI</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => onSelectCampaign(c.id)}
                    className="hover:bg-slate-700/50 cursor-pointer"
                  >
                    <td className="px-3 py-2 text-white">{c.name}</td>
                    <td className="px-3 py-2 text-slate-300">{c.sourceName ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-400 text-xs uppercase">{c.campaign_type}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{c.clicks}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{c.signups}</td>
                    <td className="px-3 py-2 text-right text-green-400 tabular-nums">${c.revenue.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-red-400 tabular-nums">${c.spend.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{c.roi.toFixed(1)}%</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-green-900/50 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                        {c.is_active ? 'active' : 'off'}
                      </span>
                    </td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-slate-500 py-10">No campaigns in this date range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
